import { useEffect, useRef, useState } from "react";
import { SlidersHorizontal, X } from "lucide-react";
import { FilterChip, Icon, SearchInput } from "@/shared/rd";

export type PlanFilterItem = { id: string; label: string; onSelect: () => void };

/**
 * The design's dashed "Filter" control with its own search. The kit has no
 * menu/popover yet, so this one is local to the module.
 */
export function PlanFilterMenu({
  label,
  searchPlaceholder,
  emptyLabel,
  items,
  query,
  onQueryChange,
}: {
  label: string;
  searchPlaceholder: string;
  emptyLabel: string;
  items: PlanFilterItem[];
  query: string;
  onQueryChange: (next: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) close();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  });

  function close() {
    setOpen(false);
    onQueryChange("");
  }

  return (
    <div ref={rootRef} style={{ position: "relative" }}>
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => (open ? close() : setOpen(true))}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          height: 28,
          padding: "0 10px",
          border: "1px dashed var(--rd-line-control-hover)",
          borderRadius: "var(--rd-radius-chip)",
          background: "transparent",
          font: "inherit",
          fontSize: 13,
          color: "var(--rd-text-secondary)",
          cursor: "pointer",
        }}
      >
        <Icon icon={SlidersHorizontal} size={14} />
        <span>{label}</span>
      </button>
      {open ? (
        <div
          style={{
            position: "absolute",
            insetInlineStart: 0,
            top: 34,
            zIndex: 20,
            width: 260,
            padding: 6,
            borderRadius: "var(--rd-radius-lg)",
            background: "var(--rd-surface)",
            boxShadow: "var(--rd-shadow-modal)",
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          <SearchInput
            autoFocus
            value={query}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            onChange={(event) => onQueryChange(event.target.value)}
          />
          <div role="menu" style={{ display: "flex", flexDirection: "column", maxHeight: 280, overflowY: "auto" }}>
            {items.length ? (
              items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="menuitem"
                  className="rd-listsec-row"
                  style={{ borderBottom: 0, borderRadius: "var(--rd-radius-md)", padding: "6px 8px", fontSize: 14, cursor: "pointer" }}
                  onClick={() => {
                    item.onSelect();
                    close();
                  }}
                >
                  <span dir="auto">{item.label}</span>
                </button>
              ))
            ) : (
              <span style={{ padding: "6px 8px", fontSize: 13, color: "var(--rd-text-tertiary)" }}>{emptyLabel}</span>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** An applied filter as a dark chip that removes itself. */
export function AppliedFilter({
  text,
  removeLabel,
  onRemove,
}: {
  text: string;
  removeLabel: string;
  onRemove: () => void;
}) {
  return (
    <FilterChip active aria-label={`${removeLabel}: ${text}`} onClick={onRemove} style={{ paddingInlineEnd: 8 }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span dir="auto">{text}</span>
        <Icon icon={X} size={14} />
      </span>
    </FilterChip>
  );
}
